import {
  createContext,
  type MutableRefObject,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { useThumbnailManualReady } from '@elfsight/configurator-preview';
import { useSettings } from '@wu/widget-core';

type ImageLazyLoadingContextType = {
  setupImageLazyLoading: (
    imageId: string,
    lazyLoadImageRef: MutableRefObject<() => void>
  ) => void;
  handleChangeInVisibleImages: (newlyVisibleImagesSources: string[]) => void;
  reportImageLoaded: (imageId: string) => void;
  allImagesLoaded: boolean;
};

const ImageLazyLoadingContext = createContext<ImageLazyLoadingContextType>({
  setupImageLazyLoading: () => {},
  handleChangeInVisibleImages: () => {},
  reportImageLoaded: () => {},
  allImagesLoaded: false
});

export function ImageLazyLoadingProvider({ children }) {
  const { renderThumbnailMode } = useSettings();
  const [enableThumbnailReadyListener, resolveThumbnailReadyListener] =
    useThumbnailManualReady(renderThumbnailMode);

  const registeredImagesRef = useRef(new Map());
  const loadingImagesRef = useRef(new Set());
  const visibleLoadingImagesRef = useRef(new Set());
  const loadedImagesRef = useRef(new Set());

  const [allImagesLoaded, setAllImagesLoaded] = useState(false);

  useEffect(() => {
    enableThumbnailReadyListener();
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setupImageLazyLoading = (imageId, lazyLoadImageRef) => {
    if (
      loadingImagesRef.current.has(imageId) ||
      loadedImagesRef.current.has(imageId)
    ) {
      lazyLoadImageRef.current();

      return;
    }

    if (registeredImagesRef.current.has(imageId)) {
      registeredImagesRef.current.get(imageId).push(lazyLoadImageRef);
    } else {
      registeredImagesRef.current.set(imageId, [lazyLoadImageRef]);
    }
  };

  const startLoadingImage = (imageId) => {
    const loadImageHandlerRefs = registeredImagesRef.current.get(imageId);

    if (!loadedImagesRef.current.has(imageId)) {
      loadingImagesRef.current.add(imageId);
      visibleLoadingImagesRef.current.add(imageId);
      setAllImagesLoaded(visibleLoadingImagesRef.current.size === 0);
    }

    if (loadImageHandlerRefs) {
      loadImageHandlerRefs.forEach((loadImageRef) => {
        loadImageRef.current();
      });

      registeredImagesRef.current.delete(imageId);
    }
  };

  const handleChangeInVisibleImages = (newlyVisibleImagesSources) => {
    visibleLoadingImagesRef.current.clear();

    newlyVisibleImagesSources.forEach((imageId) => {
      startLoadingImage(imageId);
    });
  };

  const reportImageLoaded = (imageId) => {
    loadedImagesRef.current.add(imageId);
    loadingImagesRef.current.delete(imageId);
    visibleLoadingImagesRef.current.delete(imageId);

    const allImagesLoaded = visibleLoadingImagesRef.current.size === 0;
    setAllImagesLoaded(allImagesLoaded);
    if (allImagesLoaded) {
      // Немного ждем перестроения лейаута
      setTimeout(() => resolveThumbnailReadyListener(), 1000);
    }
  };

  const providerValue = useMemo(
    () => ({
      setupImageLazyLoading,
      handleChangeInVisibleImages,
      reportImageLoaded,
      allImagesLoaded
    }),
    [
      setupImageLazyLoading,
      handleChangeInVisibleImages,
      reportImageLoaded,
      allImagesLoaded
    ]
  );

  return (
    <ImageLazyLoadingContext.Provider value={providerValue}>
      {children}
    </ImageLazyLoadingContext.Provider>
  );
}

export function useImageLazyLoading() {
  return useContext(ImageLazyLoadingContext);
}
